import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

export interface Review {
  _id?: string
  projectId: string
  userId: string
  userName: string
  userProfileImageUrl?: string
  rating: number
  content: string
  createdAt?: string
  updatedAt?: string
}

export const useReviews = (projectId: string) => {
  const [reviews, setReviews] = useState<Review[]>([])
  const [averageRating, setAverageRating] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchReviews = async () => {
    if (!projectId) return
    
    setLoading(true)
    try {
      const { list } = await lumi.entities.reviews.list({
        filter: { projectId },
        sort: { createdAt: -1 }
      })
      setReviews(list)
      
      // Calculate average rating
      if (list.length > 0) {
        const avg = list.reduce((sum, review) => sum + review.rating, 0) / list.length
        setAverageRating(Math.round(avg * 10) / 10)
      } else {
        setAverageRating(0)
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReviews()
  }, [projectId])

  const addReview = async (reviewData: Omit<Review, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // Check if user already reviewed
      const existingReview = reviews.find(r => r.userId === reviewData.userId)
      if (existingReview) {
        throw new Error('You have already reviewed this project')
      }

      const newReview = await lumi.entities.reviews.create({
        ...reviewData,
        creator: reviewData.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchReviews()
      return newReview
    } catch (error) {
      console.error('Failed to add review:', error)
      throw error
    }
  }

  const updateReview = async (reviewId: string, updates: Partial<Review>) => {
    if (typeof reviewId !== 'string') {
      throw new Error('Review ID must be a string')
    }
    
    try {
      const updated = await lumi.entities.reviews.update(reviewId, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      await fetchReviews()
      return updated
    } catch (error) {
      console.error('Failed to update review:', error)
      throw error
    }
  }

  const deleteReview = async (reviewId: string) => {
    if (typeof reviewId !== 'string') {
      throw new Error('Review ID must be a string')
    }
    
    try {
      await lumi.entities.reviews.delete(reviewId)
      await fetchReviews()
    } catch (error) {
      console.error('Failed to delete review:', error)
      throw error
    }
  }

  return {
    reviews,
    averageRating,
    loading,
    fetchReviews,
    addReview,
    updateReview,
    deleteReview
  }
}
